import { Link } from "react-router-dom";
import { useEffect } from "react";
import SellNavbar from "@/components/sell/SellNavbar";
import SellHero from "@/components/sell/SellHero";
import SellTargetAudience from "@/components/sell/SellTargetAudience";
import SellFeatures from "@/components/sell/SellFeatures";
import SellResults from "@/components/sell/SellResults";
import SellProcess from "@/components/sell/SellProcess";
import SellBeforeAfter from "@/components/sell/SellBeforeAfter";
import SellSections from "@/components/sell/SellSections";
import SellGallery from "@/components/sell/SellGallery";
import SellGuarantee from "@/components/sell/SellGuarantee";
import SellTestimonials from "@/components/sell/SellTestimonials";
import SellPricing from "@/components/sell/SellPricing";
import SellComparison from "@/components/sell/SellComparison";
import SellPerformance from "@/components/sell/SellPerformance";
import SellROICalculator from "@/components/sell/SellROICalculator";
import SellFAQ from "@/components/sell/SellFAQ";
import SellContact from "@/components/sell/SellContact";
import SellStickyMobileCTA from "@/components/sell/SellStickyMobileCTA";
import SellFloatingContact from "@/components/sell/SellFloatingContact";
import SellFomoToasts from "@/components/sell/SellFomoToasts";
import SellExitIntent from "@/components/sell/SellExitIntent";
import SellScrollProgress from "@/components/sell/SellScrollProgress";
import SellCookieConsent from "@/components/sell/SellCookieConsent";
import SellCountdownBanner from "@/components/sell/SellCountdownBanner";
import SellVideoTestimonial from "@/components/sell/SellVideoTestimonial";
import SellTrustStrip from "@/components/sell/SellTrustStrip";
import SellCaseStudy from "@/components/sell/SellCaseStudy";
import SellPressSection from "@/components/sell/SellPressSection";
import SellLeadMagnet from "@/components/sell/SellLeadMagnet";
import SellPersonalizer from "@/components/sell/SellPersonalizer";
import SellAnalyticsPreview from "@/components/sell/SellAnalyticsPreview";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Clock, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const sellJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Folio Studio — Création de Portfolio Professionnel",
  "description": "Service de création de portfolios professionnels haut de gamme. Design premium, animations fluides, visible sur Google, livré en 48h. À partir de 79€.",
  "provider": {
    "@type": "ProfessionalService",
    "name": "Folio Studio",
    "url": "https://foliostudio.fr/sell"
  },
  "areaServed": "FR",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Formules Portfolio",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "Starter",
        "price": "79",
        "priceCurrency": "EUR",
        "description": "Personnalisation basique, prêt à publier sous 72h"
      },
      {
        "@type": "Offer",
        "name": "Clé en main",
        "price": "149",
        "priceCurrency": "EUR",
        "description": "Portfolio complet et optimisé, bilingue FR/EN, livré en 48h"
      },
      {
        "@type": "Offer",
        "name": "Sur-mesure",
        "price": "349",
        "priceCurrency": "EUR",
        "description": "Portfolio unique avec fonctionnalités custom"
      }
    ]
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "ratingCount": "50",
    "bestRating": "5"
  }
};

const Sell = () => {
  useEffect(() => {
    document.title = "Folio Studio — Votre Vitrine Professionnelle Haut de Gamme";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Création de portfolios professionnels premium. Design unique, animations fluides, visible sur Google, livré en 48h. À partir de 79€. Satisfait ou remboursé.");
    }

    const existingScript = document.querySelector('script[data-sell-jsonld]');
    if (!existingScript) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-sell-jsonld", "true");
      script.textContent = JSON.stringify(sellJsonLd);
      document.head.appendChild(script);
    }

    // Disable right-click on protected content
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.protected-content')) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      const s = document.querySelector('script[data-sell-jsonld]');
      if (s) s.remove();
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden relative sell-typography">
      {/* ═══ Page-level ambient background ═══ */}
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
        <div className="absolute top-[-10%] left-[-15%] w-[800px] h-[800px] bg-primary/[0.07] rounded-full blur-[200px]" />
        <div className="absolute top-[5%] right-[-10%] w-[600px] h-[600px] bg-accent/[0.05] rounded-full blur-[180px]" />
        <div className="absolute top-[40%] left-[50%] -translate-x-1/2 w-[1000px] h-[400px] bg-primary/[0.03] rounded-full blur-[200px]" />
        <div className="absolute bottom-[10%] right-[-5%] w-[700px] h-[700px] bg-accent/[0.04] rounded-full blur-[200px]" />
        <div className="absolute bottom-[-5%] left-[10%] w-[500px] h-[500px] bg-primary/[0.04] rounded-full blur-[180px]" />
        <div className="absolute inset-0 opacity-[0.015] bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIxIi8+PC9zdmc+')]" />
      </div>

      <div className="relative z-10">
        <SellScrollProgress />
        <SellNavbar />
        <SellStickyMobileCTA />
        <SellFloatingContact />
        <SellFomoToasts />
        <SellExitIntent />
        <SellCookieConsent />

      <div className="pt-16">
        <SellCountdownBanner />
        <SellHero />
        <SellTrustStrip />
        <SellPressSection />

        <div id="audience" className="scroll-mt-20"><SellTargetAudience /></div>
        <div id="features" className="scroll-mt-20"><SellFeatures /></div>
        <SellResults />
        <div id="process" className="scroll-mt-20"><SellProcess /></div>
         <div id="compare" className="scroll-mt-20"><SellBeforeAfter /></div>
        <SellSections />
        <div id="gallery" className="scroll-mt-20"><SellGallery /></div>
        
        <div id="personalizer" className="scroll-mt-20"><SellPersonalizer /></div>
        <div id="case-study" className="scroll-mt-20"><SellCaseStudy /></div>
        <SellAnalyticsPreview />
        <div id="performance" className="scroll-mt-20"><SellPerformance /></div>
        <div id="comparison" className="scroll-mt-20"><SellComparison /></div>
        <div id="roi" className="scroll-mt-20"><SellROICalculator /></div>
        <SellLeadMagnet />
        <SellGuarantee />
        <SellVideoTestimonial />
        <div id="testimonials" className="scroll-mt-20"><SellTestimonials /></div>
        <SellPricing />
        <div id="faq" className="scroll-mt-20"><SellFAQ /></div>

        {/* Final CTA */}
        <section className="py-20 sm:py-24">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center p-8 sm:p-12 md:p-16 rounded-3xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border border-primary/20 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-60 h-60 bg-primary/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-60 h-60 bg-accent/10 rounded-full blur-3xl" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
              
              <div className="relative">
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-bold mb-6"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Sparkles className="w-4 h-4" />
                  Offre de lancement — places limitées
                </motion.div>

                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-5 sm:mb-6 leading-tight tracking-tight">
                  Ne laissez plus vos concurrents
                  <br />
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    vous voler des clients
                  </span>
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base lg:text-lg mb-4 max-w-xl mx-auto">
                  Chaque jour sans portfolio premium, c'est un prospect qui choisit un concurrent avec une meilleure image en ligne.
                </p>
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-[10px] sm:text-xs lg:text-sm text-muted-foreground mb-6 sm:mb-8">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                    Satisfait ou remboursé
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                    Livré en 48h
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                    4.9/5 de satisfaction
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                  <a href="#contact">
                    <Button size="lg" className="text-sm sm:text-base lg:text-lg px-6 sm:px-10 py-5 sm:py-7 bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group w-full sm:w-auto font-bold">
                      Oui, je veux mon portfolio
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                  <a href="/cv" target="_blank" rel="noopener">
                    <Button size="lg" variant="outline" className="text-sm sm:text-base px-6 sm:px-8 py-5 sm:py-7 border-primary/30 hover:bg-primary/10 w-full sm:w-auto font-semibold">
                      Voir la démo live
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <div id="contact" className="scroll-mt-20">
          <SellContact />
        </div>

        {/* Premium Footer */}
        <footer className="py-12 sm:py-16 border-t border-border/20">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 mb-10 sm:mb-12">
              <div>
                <div className="flex items-center gap-2 font-extrabold text-lg mb-4">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Folio Studio
                  </span>
                </div>
                <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed mb-4">
                  Création de portfolios professionnels haut de gamme pour tous les métiers. 
                  Design premium, livré en 48h, visible sur Google.
                </p>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                  <span className="font-semibold">4.9/5</span>
                  <span>— 50+ clients satisfaits</span>
                </div>
              </div>

              <div>
                <h4 className="font-bold mb-4 text-sm sm:text-base">Navigation</h4>
                <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground">
                  {[
                    { href: "#features", label: "Fonctionnalités" },
                    { href: "#process", label: "Comment ça marche" },
                    { href: "#gallery", label: "Exemples" },
                    { href: "#performance", label: "Performances" },
                    { href: "#comparison", label: "Comparatif" },
                    { href: "#pricing", label: "Tarifs" },
                    { href: "#faq", label: "FAQ" },
                     { href: "#contact", label: "Contact" },
                   ].map(l => (
                    <li key={l.href}>
                      <a href={l.href} className="hover:text-primary transition-colors">{l.label}</a>
                    </li>
                  ))}
                  <li>
                    <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold mb-4 text-sm sm:text-base">Confiance & Sécurité</h4>
                <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                    Paiement sécurisé (Stripe)
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                    Données chiffrées (SSL)
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                    Conforme RGPD
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                    Satisfait ou remboursé
                  </li>
                </ul>
                <Link 
                  to="/legal" 
                  className="inline-block mt-4 text-xs sm:text-sm text-primary hover:underline underline-offset-4 font-semibold"
                >
                  Mentions légales & CGV →
                </Link>
              </div>
            </div>

            <div className="border-t border-border/30 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
              <span>© {new Date().getFullYear()} Folio Studio. Tous droits réservés.</span>
              <a href="/cv" target="_blank" rel="noopener" className="text-primary hover:underline underline-offset-4 font-semibold">
                Voir la démo live →
              </a>
            </div>
          </div>
        </footer>
      </div>
      </div>
    </div>
  );
};

export default Sell;
